import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { ICoachTypes } from '../onboarding/onboardingSlice';
import { IBrandConfigurations, ICoachServicePackage } from '../coach/coachSlice';
import { DayOfWeekEnum } from '../../types/booking';
import {
  removeBrandConfigurations,
  setBrandConfigurations,
} from '../../utils/brandConfigurationsUtils';

export interface ICoachPublicDetails {
  id: string;
  firstName: string;
  lastName: string;
  profilePic: string;
  minimumNoticeTime: number;
  coachTypes: ICoachTypes[];
  servicePackages: ICoachServicePackage[];
  officeAddress?: string | null;
  officePhone?: string | null;
  calendar: string | null;
  videoCallPlatform: string | null;
  isProvideOnlineConsultations?: boolean;
  isProvideInPersonConsultations?: boolean;
  brandConfigurations?: IBrandConfigurations;
}

export interface ICoachMiniWebBooking {
  username: string;
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  clientPhone?: string;
  date: string;
  duration: number;
  servicePackageId: string;
  isAgreedToTerms: boolean;
  isSubscribedToNewsletter?: boolean;
  referenceText?: string;
  referenceNote?: string;
}

export interface ICoachMiniWebBookingInput {
  coachMiniWebBooking: ICoachMiniWebBooking;
  referenceFile?: File[] | null;
}

export interface ICoachMiniWebBookingData {
  id?: string;
  servicePackage?: ICoachServicePackage;
  date?: string;
  duration?: number;
}
export interface ICoachUnavailabilities {
  date: number;
  duration: number;
}

export interface ICoachAvailableTimes {
  date: Date;
}

export interface IAvailability {
  startTime: number;
  endTime: number;
  dayOfWeek: DayOfWeekEnum;
}

export interface IGetBookings {
  coachId: string;
  date: Date;
}

export interface IGetBookingsForSelectedMonth {
  username: string;
  month: number;
  year: number;
}

export interface IDates {
  date: Date;
}

interface IMiniwebState {
  error: string | null;
  getPublicDetailsLoading: boolean;
  coachPublicDetails: ICoachPublicDetails;
  createMiniwebBookingLoading: boolean;
  coachMiniWebBookingSuccess: boolean;
  getCoachUnavalabilitiesLoading: boolean;
  coachUnavailabilitiesForDay: ICoachUnavailabilities[];
  availableTimes: Date[];
  getCoachUnavalabilitiesForDayLoading: boolean;
  coachAvailabilities?: IAvailability[];
  fetchCoachAvailabilitiesLoading: boolean;
  fetchCoachAvailabilitiesSuccess: boolean;
  selectedDateInMiniweb: string;
  coachFullBookedDays: Date[];
  fetchBookingsByMonthForMiniwebLoading: boolean;
  bookingData: ICoachMiniWebBookingData;
  isUsernameInvalid: boolean;
}

const initialState: IMiniwebState = {
  error: null,
  getPublicDetailsLoading: false,
  coachPublicDetails: {
    id: '',
    firstName: '',
    lastName: '',
    profilePic: '',
    minimumNoticeTime: 0,
    coachTypes: [],
    servicePackages: [],
    officeAddress: '',
    officePhone: '',
    calendar: null,
    videoCallPlatform: null,
  },
  createMiniwebBookingLoading: false,
  coachMiniWebBookingSuccess: false,
  getCoachUnavalabilitiesLoading: false,
  coachUnavailabilitiesForDay: [],
  availableTimes: [],
  getCoachUnavalabilitiesForDayLoading: false,
  fetchCoachAvailabilitiesLoading: false,
  fetchCoachAvailabilitiesSuccess: false,
  fetchBookingsByMonthForMiniwebLoading: false,
  selectedDateInMiniweb: dayjs().toISOString(),
  coachFullBookedDays: [],
  bookingData: {},
  isUsernameInvalid: false,
};

const miniwebSlice = createSlice({
  name: 'miniweb',
  initialState,
  reducers: {
    getCoachPublicDetailsRequest: (
      state: IMiniwebState,
      _action: PayloadAction<{ username: string }>
    ) => {
      state.getPublicDetailsLoading = true;
      state.isUsernameInvalid = false;
    },
    getCoachPublicDetailsSuccess: (
      state: IMiniwebState,
      action: PayloadAction<ICoachPublicDetails>
    ) => {
      state.getPublicDetailsLoading = false;
      state.coachPublicDetails = action.payload;
      if (action.payload.brandConfigurations) {
        const { brandTag, brandNameLogo, logo } = action.payload.brandConfigurations;
        setBrandConfigurations(brandTag, brandNameLogo, logo);
      } else {
        removeBrandConfigurations();
      }
      state.isUsernameInvalid = false;
    },
    getCoachPublicDetailsFailure: (state: IMiniwebState, action: PayloadAction<string>) => {
      state.getPublicDetailsLoading = false;
      state.error = action.payload;
      if (action.payload.includes('Coach not found for the given Id')) {
        state.isUsernameInvalid = true;
      } else {
        state.isUsernameInvalid = false;
      }
    },
    createCoachMiniwebBookingRequest: (
      state: IMiniwebState,
      _action: PayloadAction<ICoachMiniWebBookingInput>
    ) => {
      state.createMiniwebBookingLoading = true;
      state.coachMiniWebBookingSuccess = false;
      state.bookingData = {};
    },
    createCoachMiniwebBookingSuccess: (
      state: IMiniwebState,
      action: PayloadAction<ICoachMiniWebBookingData>
    ) => {
      state.createMiniwebBookingLoading = false;
      state.coachMiniWebBookingSuccess = true;
      state.bookingData = action.payload;
    },
    createCoachMiniwebBookingFailure: (state: IMiniwebState, action: PayloadAction<string>) => {
      state.createMiniwebBookingLoading = false;
      state.coachMiniWebBookingSuccess = false;
      state.error = action.payload;
    },
    createCoachMiniwebBookingReset: (state: IMiniwebState) => {
      state.createMiniwebBookingLoading = false;
      state.coachMiniWebBookingSuccess = false;
    },
    getAvailableTimesRequest: (
      state: IMiniwebState,
      _action: PayloadAction<{
        username: string;
        date: number;
        month: number;
        year: number;
        duration: number;
      }>
    ) => {
      state.getCoachUnavalabilitiesForDayLoading = true;
    },
    getAvailableTimesRequestSuccess: (
      state: IMiniwebState,
      action: PayloadAction<ICoachAvailableTimes[]>
    ) => {
      state.getCoachUnavalabilitiesForDayLoading = false;
      state.availableTimes = action.payload?.map((i) => i.date);
    },
    getAvailableTimesRequestFailure: (state: IMiniwebState, action: PayloadAction<string>) => {
      state.getCoachUnavalabilitiesForDayLoading = false;
      state.error = action.payload;
      state.availableTimes = [];
    },
    fetchCoachAvailabilitiesForMiniweb(
      state: IMiniwebState,
      _action: PayloadAction<{ username: string }>
    ) {
      state.coachAvailabilities = [];
      state.fetchCoachAvailabilitiesLoading = true;
    },
    fetchCoachAvailabilitiesForMiniwebSuccess(
      state: IMiniwebState,
      action: PayloadAction<IAvailability[]>
    ) {
      state.coachAvailabilities = action.payload;
      state.fetchCoachAvailabilitiesLoading = false;
      state.fetchCoachAvailabilitiesSuccess = true;
    },
    fetchCoachAvailabilitiesForMiniwebFailure(state: IMiniwebState) {
      state.fetchCoachAvailabilitiesLoading = false;
      state.fetchCoachAvailabilitiesSuccess = false;
    },
    selectDateInMiniweb(state: IMiniwebState, action: PayloadAction<{ date: string }>) {
      state.selectedDateInMiniweb = action.payload.date;
    },
    fetchBookingsByMonthForMiniweb(
      state: IMiniwebState,
      _action: PayloadAction<IGetBookingsForSelectedMonth>
    ) {
      state.coachFullBookedDays = [];
      state.fetchBookingsByMonthForMiniwebLoading = true;
    },
    fetchBookingsByMonthForMiniwebSuccess(state: IMiniwebState, action: PayloadAction<IDates[]>) {
      state.coachFullBookedDays = action.payload.map((date) => date.date);
      state.fetchBookingsByMonthForMiniwebLoading = false;
    },
    fetchBookingsByMonthForMiniwebFailure(state: IMiniwebState) {
      state.fetchBookingsByMonthForMiniwebLoading = false;
    },
  },
});

export const {
  getCoachPublicDetailsRequest,
  getCoachPublicDetailsSuccess,
  getCoachPublicDetailsFailure,
  createCoachMiniwebBookingRequest,
  createCoachMiniwebBookingSuccess,
  createCoachMiniwebBookingFailure,
  getAvailableTimesRequest,
  getAvailableTimesRequestSuccess,
  getAvailableTimesRequestFailure,
  fetchCoachAvailabilitiesForMiniweb,
  fetchCoachAvailabilitiesForMiniwebSuccess,
  fetchCoachAvailabilitiesForMiniwebFailure,
  selectDateInMiniweb,
  fetchBookingsByMonthForMiniweb,
  fetchBookingsByMonthForMiniwebSuccess,
  fetchBookingsByMonthForMiniwebFailure,
  createCoachMiniwebBookingReset,
} = miniwebSlice.actions;

const miniwebActions = miniwebSlice.actions;
const miniwebReducer = miniwebSlice.reducer;

export { miniwebActions, miniwebReducer };
